import { format, isValid } from 'date-fns';
import { iFilterItem } from '../types/filterTypes';

const NO_VALUE_OPERATORS = ['isEmpty', 'isNotEmpty'];
const NEGATED_OPERATORS = ['doesNotContain', 'doesNotEqual', '!=', 'not'];
const NUMBER_OPERATORS = ['=', '!=', '>', '>=', '<', '<='];
const DATE_OPERATORS = ['after', 'onOrAfter', 'before', 'onOrBefore'];
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}/;

const OPERATOR_WORDS: Record<string, string> = {
  contains: 'contains',
  doesNotContain: 'does not contain',
  equals: 'is',
  doesNotEqual: 'is not',
  startsWith: 'starts with',
  endsWith: 'ends with',
  isEmpty: 'is empty',
  isNotEmpty: 'is not empty',
  isAnyOf: 'is any of',
  is: 'is',
  not: 'is not',
  after: 'after',
  onOrAfter: 'on or after',
  before: 'before',
  onOrBefore: 'on or before',
  '=': '=',
  '!=': '≠',
  '>': '>',
  '>=': '≥',
  '<': '<',
  '<=': '≤',
};

const isBlank = (value: unknown) =>
  value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

const toDay = (value: unknown) => {
  if (isBlank(value)) return null;
  const text = String(value);
  const date = value instanceof Date ? value : new Date(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T00:00:00` : text);
  return isValid(date) ? format(date, 'yyyy-MM-dd') : null;
};

const filterValues = (item: iFilterItem) => (Array.isArray(item.value) ? item.value : [item.value]).map(String);

const isDateRule = (item: iFilterItem) =>
  DATE_OPERATORS.includes(item.operator) ||
  (['is', 'not'].includes(item.operator) && DAY_PATTERN.test(String(item.value ?? '')));

export const isLegacyRuleActive = (item: iFilterItem) =>
  NO_VALUE_OPERATORS.includes(item.operator) || !isBlank(item.value);

const matchesOne = (value: unknown, item: iFilterItem): boolean => {
  const { operator } = item;
  if (operator === 'isEmpty') return isBlank(value);
  if (operator === 'isNotEmpty') return !isBlank(value);

  if (isDateRule(item)) {
    const day = toDay(value);
    const target = toDay(item.value);
    if (!day || !target) return false;
    if (operator === 'is') return day === target;
    if (operator === 'not') return day !== target;
    if (operator === 'after') return day > target;
    if (operator === 'onOrAfter') return day >= target;
    if (operator === 'before') return day < target;
    return day <= target;
  }

  if (NUMBER_OPERATORS.includes(operator)) {
    if (isBlank(value)) return operator === '!=';
    const number = Number(value);
    const target = Number(item.value);
    if (operator === '=') return number === target;
    if (operator === '!=') return number !== target;
    if (operator === '>') return number > target;
    if (operator === '>=') return number >= target;
    if (operator === '<') return number < target;
    return number <= target;
  }

  const text = isBlank(value) ? null : String(value).toLowerCase();
  const targets = filterValues(item).map((target) => target.toLowerCase());
  const target = targets[0] ?? '';
  switch (operator) {
    case 'contains':
      return text !== null && text.includes(target);
    case 'doesNotContain':
      return text === null || !text.includes(target);
    case 'equals':
    case 'is':
      return text === target;
    case 'doesNotEqual':
    case 'not':
      return text !== target;
    case 'startsWith':
      return text !== null && text.startsWith(target);
    case 'endsWith':
      return text !== null && text.endsWith(target);
    case 'isAnyOf':
      return text !== null && targets.includes(text);
    default:
      return true;
  }
};

export const matchesLegacyRule = (value: unknown, item: iFilterItem): boolean => {
  const values = Array.isArray(value) ? value : [value];
  if (item.operator === 'isEmpty') return values.every(isBlank);
  if (item.operator === 'isNotEmpty') return values.some((entry) => !isBlank(entry));
  if (!values.length) return matchesOne(null, item);
  return NEGATED_OPERATORS.includes(item.operator)
    ? values.every((entry) => matchesOne(entry, item))
    : values.some((entry) => matchesOne(entry, item));
};

export const describeLegacyRule = (item: iFilterItem, fieldLabel: string) => {
  const words = OPERATOR_WORDS[item.operator] ?? item.operator;
  if (NO_VALUE_OPERATORS.includes(item.operator)) return `${fieldLabel} ${words}`;
  const shown = filterValues(item).map((value) => {
    const day = isDateRule(item) ? toDay(value) : null;
    return day ? format(new Date(`${day}T00:00:00`), 'dd MMM yyyy') : value;
  });
  return `${fieldLabel} ${words} ${shown.join(', ')}`;
};
